// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  CreateAggregatorProposalOperationRequestBody,
  CreateInvestmentProposalRequestBody,
  InternalCreateProposalRequestBody,
  InternalProposalOperation,
  ProposalPortfolio,
} from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import { toCashLegIdentifier } from './map-target-portfolio-request';

const mapOperation = (
  operation: InternalProposalOperation,
): CreateAggregatorProposalOperationRequestBody => {
  const fromTicker =
    operation.operation_type === 'buy'
      ? toCashLegIdentifier(operation.from_ticker)
      : operation.from_ticker;
  const fromIsin =
    operation.operation_type === 'buy'
      ? toCashLegIdentifier(operation.from_isin)
      : operation.from_isin;
  const toTicker =
    operation.operation_type === 'sell'
      ? toCashLegIdentifier(operation.to_ticker)
      : operation.to_ticker;
  const toIsin =
    operation.operation_type === 'sell'
      ? toCashLegIdentifier(operation.to_isin)
      : operation.to_isin;

  return {
    input_type: operation.input_type ?? 'shares',
    operation_type: operation.operation_type,
    from_iban: operation.from_iban,
    from_ticker: fromTicker ?? null,
    from_isin: fromIsin ?? null,
    to_iban: operation.to_iban,
    to_ticker: toTicker ?? null,
    to_isin: toIsin ?? null,
    quantity: operation.quantity,
    price: operation.price,
    amount: operation.amount,
  };
};

const mapPortfolio = (portfolio: ProposalPortfolio): ProposalPortfolio => ({
  returns_3m: portfolio.returns_3m,
  returns_6m: portfolio.returns_6m,
  returns_1y: portfolio.returns_1y,
  returns_3y: portfolio.returns_3y,
  returns_ytd: portfolio.returns_ytd,
  volatility: portfolio.volatility,
  max_drawdown: portfolio.max_drawdown,
  positions: portfolio.positions,
  markets: portfolio.markets,
  currencies: portfolio.currencies,
  countries: portfolio.countries,
  segments: portfolio.segments,
});

/**
 * Maps the internal create-proposal payload (`institution_id` on operations, cash
 * currency tickers) to the advisory `ProposalCreate` wire body.
 */
export const mapInvestmentProposalRequest = (
  payload: InternalCreateProposalRequestBody,
): CreateInvestmentProposalRequestBody => ({
  name: payload.name,
  description: payload.description,
  currency: payload.currency,
  operations: payload.operations.map(mapOperation),
  current_portfolio: mapPortfolio(payload.current_portfolio),
  proposal: mapPortfolio(payload.proposal),
  returns: payload.returns,
  risk_returns: payload.risk_returns,
});
